import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { Check } from '@ankr.com/ui';

import { Placeholder } from 'modules/common/components/Placeholder';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { planButtonTranslation } from './translation';
import { usePlanButtonStyles } from './usePlanButtonStyles';

export interface PlanButtonProps {
  children?: string;
  className?: string;
  disabled?: boolean;
  isCurrentPlan?: boolean;
  isCurrentPlanTextShort?: boolean;
  onClick: () => void;
  startIcon?: React.ReactNode;
  variant?: ButtonProps['variant'];
}

export const PlanButton = ({
  children,
  className = '',
  disabled,
  isCurrentPlan,
  isCurrentPlanTextShort,
  onClick,
  startIcon,
  variant = 'outlined',
}: PlanButtonProps) => {
  const { isAccountInitializing } = useAccount();
  const { initalLoading: isAccountStatusLoading } = useAccountStatus();

  const { classes, cx } = usePlanButtonStyles(undefined, {
    props: { classes: { root: className } },
  });
  const { keys, t } = useTranslation(planButtonTranslation);

  const currentPlanText = isCurrentPlanTextShort
    ? t(keys.shortCurrentPlan)
    : t(keys.currentPlan);

  const loading = isAccountInitializing || isAccountStatusLoading;

  const icon = isCurrentPlan ? <Check /> : startIcon;

  return (
    <Button
      className={cx(classes.root, { [classes.currentPlan]: isCurrentPlan })}
      disabled={disabled || loading || isCurrentPlan}
      fullWidth
      onClick={onClick}
      startIcon={
        loading ? <CircularProgress size={18} color="inherit" /> : icon
      }
      variant={variant}
    >
      <Placeholder hasPlaceholder={loading} placeholder={null}>
        {isCurrentPlan ? currentPlanText : children}
      </Placeholder>
    </Button>
  );
};
